import React, { useEffect, useState } from 'react'
import {
  CCard,
  CCardBody,
  CRow,
  CCol,
  CButton,
  CLoadingButton,
  CFormInput,
  CFormLabel,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilPlus, cilTrash, cilInfo } from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import CommonService from 'src/service/CommonService'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

let rowSeq = 0
const emptyRow = () => ({ key: ++rowSeq, first_name: '', last_name: '', email: '' })

const InfoBox = ({ children }) => (
  <div
    className="d-flex align-items-start gap-2 rounded-3 p-3 mb-3"
    style={{ backgroundColor: 'rgba(13,110,253,0.08)' }}
  >
    <CIcon icon={cilInfo} className="flex-shrink-0 mt-1 text-primary" />
    <div className="small">{children}</div>
  </div>
)

// "Add People" tab of the Invitation step: add several guests at once instead of one at a time.
// Each filled row is submitted to the existing single-add endpoint (which already handles the
// per-email duplicate check and Game Code creation), so no backend changes were needed.
const AddMultiplePeople = ({ visibility, fetchData, onSummaryChange }) => {
  const [rows, setRows] = useState([emptyRow()])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    onSummaryChange?.(rows.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length])

  useEffect(() => () => onSummaryChange?.(null), []) // eslint-disable-line react-hooks/exhaustive-deps

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)))
  }

  const addRow = () => setRows((prev) => [...prev, emptyRow()])

  const removeRow = (key) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev))
  }

  const handleSubmit = async () => {
    const candidates = rows
      .map((r) => ({
        first_name: r.first_name.trim(),
        last_name: r.last_name.trim(),
        email: r.email.trim(),
      }))
      .filter((r) => r.email !== '')

    if (!candidates.length) {
      ToastComponent('Add at least one email address.', 'error')
      return
    }
    const invalid = candidates.find((r) => !EMAIL_RE.test(r.email))
    if (invalid) {
      ToastComponent(`"${invalid.email}" is not a valid email address.`, 'error')
      return
    }

    // Requests below run in parallel, so two rows with the same email would both pass the
    // server's duplicate check before either is saved. Dedupe within this batch first.
    const seen = new Set()
    const deduped = candidates.filter((r) => {
      const key = r.email.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    if (deduped.length !== candidates.length) {
      ToastComponent('Removed duplicate email addresses within this batch.', 'error')
    }

    setSaving(true)
    const results = await Promise.all(
      deduped.map((r) =>
        CommonService.saveJoinCodeMember(r)
          .then((res) => ({ ok: res.status === 200, row: r, message: res.message }))
          .catch(() => ({ ok: false, row: r, message: 'Something went wrong.' })),
      ),
    )
    setSaving(false)

    const added = results.filter((r) => r.ok).length
    const failed = results.filter((r) => !r.ok)

    if (added) {
      ToastComponent(`${added} guest${added === 1 ? '' : 's'} added successfully.`, 'success')
      fetchData?.()
    }
    failed.forEach((f) => ToastComponent(`${f.row.email}: ${f.message}`, 'error'))

    setRows(failed.length ? failed.map((f) => ({ ...f.row, key: ++rowSeq })) : [emptyRow()])
  }

  return (
    <CCard className="border-secondary-subtle rounded-4">
      <CCardBody>
        <div className="fw-semibold fs-6 mb-1">Add People</div>
        <div className="small mb-3">
          Add people individually before reviewing and sending their invitations.
        </div>

        {rows.map((row, idx) => (
          <CRow key={row.key} className="g-3 mb-2 align-items-end">
            <CCol md={4}>
              {idx === 0 && <CFormLabel className="fw-semibold">First Name</CFormLabel>}
              <CFormInput
                placeholder="First name"
                value={row.first_name}
                onChange={(e) => updateRow(row.key, 'first_name', e.target.value)}
              />
            </CCol>
            <CCol md={3}>
              {idx === 0 && <CFormLabel className="fw-semibold">Last Name</CFormLabel>}
              <CFormInput
                placeholder="Last name"
                value={row.last_name}
                onChange={(e) => updateRow(row.key, 'last_name', e.target.value)}
              />
            </CCol>
            <CCol md={4}>
              {idx === 0 && <CFormLabel className="fw-semibold">Email address</CFormLabel>}
              <CFormInput
                placeholder="Email address"
                value={row.email}
                onChange={(e) => updateRow(row.key, 'email', e.target.value)}
              />
            </CCol>
            <CCol md={1} className="d-flex">
              <CButton
                color="danger"
                variant="outline"
                disabled={rows.length === 1}
                onClick={() => removeRow(row.key)}
              >
                <CIcon icon={cilTrash} />
              </CButton>
            </CCol>
          </CRow>
        ))}

        <InfoBox>
          {visibility === 'private' ? (
            <>
              <strong>Private game:</strong> a unique, one-time Game Code will be created for each
              person when their invitation is sent.
            </>
          ) : (
            <>
              <strong>Public game:</strong> people can also join directly from the hub, but adding
              them here still creates a personal Game Code you can send.
            </>
          )}
        </InfoBox>

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <CButton color="primary" variant="outline" onClick={addRow}>
            <CIcon icon={cilPlus} className="me-1" />
            Add Another Person
          </CButton>
          <CLoadingButton color="success" loading={saving} onClick={handleSubmit}>
            Add to Invitation List
          </CLoadingButton>
        </div>
      </CCardBody>
    </CCard>
  )
}

export default AddMultiplePeople
